import { useMemo, useState } from "react";
import { skillSignal } from "@pms/shared";
import { usePeople } from "../api";
import { ErrorBox, Empty, Loading } from "../components/State";

const SIGNAL_LABEL: Record<string, string> = {
  primary: "Primary strength",
  training: "Train them",
  avoid_overload: "Can do, does not want to",
  learning: "Curious",
  risk: "Keen but unproven",
  neutral: "—",
};

export default function People() {
  const { data, loading, error } = usePeople();
  const [q, setQ] = useState("");

  const people = data?.people ?? [];
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return people;
    return people.filter(
      (p) =>
        p.displayName.toLowerCase().includes(needle) ||
        (p.roleTitle ?? "").toLowerCase().includes(needle) ||
        p.skills.some((s) => s.name.toLowerCase().includes(needle))
    );
  }, [people, q]);

  if (loading) return <Loading what="people" />;
  if (error) return <ErrorBox error={error} what="people" />;
  if (!people.length) return <Empty what="people" />;

  const gaps = people.flatMap((p) =>
    p.skills
      .filter((s) => s.competence <= 4 && (s.wantToDo >= 7 || s.wantToLearn >= 7))
      .map((s) => ({ person: p.displayName, skill: s.name, c: s.competence, l: s.wantToLearn }))
  );

  return (
    <>
      <div className="card">
        <h2>People &amp; skills</h2>
        <p>
          Three axes per skill: what they are good at, what they want to do, and what they want to
          learn. The third is what most tools miss.
        </p>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter by name, role or skill…"
          style={{ width: "100%", maxWidth: 360 }}
        />
      </div>

      {gaps.length > 0 && (
        <div className="card">
          <h2>Growth gaps</h2>
          <p>Low competence, high appetite. These are your training and hiring signals.</p>
          <table className="table">
            <thead>
              <tr>
                <th>Person</th>
                <th>Skill</th>
                <th>Competence</th>
                <th>Wants to learn</th>
              </tr>
            </thead>
            <tbody>
              {gaps.map((g, i) => (
                <tr key={i}>
                  <td>{g.person}</td>
                  <td>{g.skill}</td>
                  <td className="mono-inline">{g.c}</td>
                  <td className="mono-inline">{g.l}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {filtered.map((p) => (
        <div className="card" key={p.id}>
          <div className="row" style={{ justifyContent: "space-between", alignItems: "baseline" }}>
            <h2 style={{ margin: 0 }}>{p.displayName}</h2>
            <span className="badge">{p.roleTitle ?? "—"}</span>
          </div>
          {p.bio && <p>{p.bio}</p>}
          {p.skills.length === 0 ? (
            <p>No skills recorded.</p>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Skill</th>
                  <th>Competence</th>
                  <th>Wants to do</th>
                  <th>Wants to learn</th>
                  <th>Signal</th>
                </tr>
              </thead>
              <tbody>
                {p.skills.map((s) => {
                  const sig = skillSignal({
                    competence: s.competence,
                    wantToDo: s.wantToDo,
                    wantToLearn: s.wantToLearn,
                  });
                  return (
                    <tr key={s.skillId}>
                      <td>{s.name}</td>
                      <td className="mono-inline">{s.competence}</td>
                      <td className="mono-inline">{s.wantToDo}</td>
                      <td className="mono-inline">{s.wantToLearn}</td>
                      <td>
                        <span className="badge">{SIGNAL_LABEL[sig] ?? sig}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </>
  );
}
